import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);

  // Extract session_id from URL
  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const sessionId = getUrlParameter('session_id');

  // Poll payment status
  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      
      const response = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      const data = response.data;
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Payment successful! Thank you for your purchase.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus('processing');
      setPollingAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);

    } catch (error) {
      console.error('🔥 Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        // Retry if not the last attempt
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setPaymentStatus('error');
        toast.error('Error checking payment status. Please try again.');
      }
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setPaymentStatus('no_session');
      toast.error('No payment session found in URL.');
    }
  }, [sessionId]);

  const handleContinue = () => {
    const metadata = paymentDetails?.metadata;
    const packageId = metadata?.package_id;
    
    // Check sessionStorage for where the user came from
    const returnUrl = sessionStorage.getItem('payment_return_url');
    const paymentService = sessionStorage.getItem('payment_service');
    

    // Set payment completion flag and clean up sessionStorage
    sessionStorage.setItem('payment_just_completed', 'true');
    sessionStorage.removeItem('payment_return_url');
    sessionStorage.removeItem('payment_service');

    // Navigate based on stored information or metadata
    const serviceToNavigate = paymentService || packageId;
    const urlToNavigate = returnUrl || '/bvadm';

    if (serviceToNavigate === 'bvadm' || urlToNavigate.includes('bvadm')) {
      navigate('/bvadm', { replace: true });
    } else if (serviceToNavigate === 'bctg' || urlToNavigate.includes('bctg')) {
      navigate('/', { replace: true });
    } else if (serviceToNavigate?.startsWith('subscription_')) {
      // Force a hard reload to clear all cached user data
      window.location.href = '/homepage';
    } else {
      navigate(returnUrl || '/bvadm', { replace: true });
    }
  };

  const renderContent = () => {
    switch (paymentStatus) {
      case 'checking':
      case 'processing':
        return (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Processing Your Payment</h2>
            <p className="text-slate-600 mb-4">
              Please wait while we confirm your payment...
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                Checking payment status... (Attempt {pollingAttempts + 1}/10)
              </p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Successful!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your purchase. Your payment has been processed successfully.
            </p>
            
            {paymentDetails && (
              <div className="bg-green-50 p-6 rounded-lg mb-6 text-left">
                <h3 className="font-semibold text-green-900 mb-3">Payment Details</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <div className="flex justify-between">
                    <span>Service:</span>
                    <span className="font-medium">{paymentDetails.metadata?.package_name || 'Service Purchase'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount:</span>
                    <span className="font-medium">${paymentDetails.amount?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className="font-medium capitalize">{paymentDetails.payment_status}</span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleContinue}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Continue to Service
            </button>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Session Expired</h2>
            <p className="text-slate-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Error</h2>
            <p className="text-slate-600 mb-6">
              There was an error processing your payment. Please check your email for confirmation or contact support.
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        );

      case 'timeout':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-orange-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Payment Status Check Timeout</h2>
            <p className="text-slate-600 mb-6">
              We couldn't confirm your payment status. Please check your email for confirmation or contact support if needed.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </button>
          </div>
        );

      case 'no_session':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">No Payment Session Found</h2>
            <p className="text-slate-600 mb-6">
              No payment session was found in the URL. Please try initiating the payment again.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <img 
              src="https://emergentdigitalinklogo.s3.us-east-1.amazonaws.com/logo1.png"
              alt="Digital Ink AI" 
              className="h-8 mr-4"
            />
            <h1 className="text-xl font-semibold text-slate-900">Digital Ink AI</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;